// client/src/pages/StaffCalendar.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { format, startOfYear, endOfYear, startOfMonth, endOfMonth, isToday, startOfDay, getDay } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import BottomNav from '../components/BottomNav';
import { X, ChevronDown, ChevronUp, Filter, CalendarDays } from 'lucide-react';
import StudentPickerModal from '../components/StudentPickerModal';

export default function StaffCalendar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [masters, setMasters] = useState([]);
  const [locations, setLocations] = useState([]);
  const [times, setTimes] = useState([]);
  const [freeSlots, setFreeSlots] = useState({});
  const [bookings, setBookings] = useState([]);
  const [noShows, setNoShows] = useState([]);
  
  const [filterInstructor, setFilterInstructor] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  const [startDate] = useState(() => startOfYear(new Date()));
  const [endDate] = useState(() => endOfYear(new Date()));
  
  const [loading, setLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [expandedInstructors, setExpandedInstructors] = useState({});
  
  const [showStudentPicker, setShowStudentPicker] = useState(false);
  const [pickerMode, setPickerMode] = useState('new-booking');
  const [pickerInstructorId, setPickerInstructorId] = useState('');
  const [pickerBookingData, setPickerBookingData] = useState({ date: '', time: '', place: '' });
  
  const scrollContainerRef = useRef(null);
  const hasScrolledToToday = useRef(false);
  
  const today = startOfDay(new Date());
  const isSpecificInstructor = filterInstructor !== 'all';
  
  // Загрузка начальных данных
  useEffect(() => {
    Promise.all([
      fetch('/api/mastercars').then(r => r.ok ? r.json() : []),
      fetch('/api/locations').then(r => r.ok ? r.json() : []),
      fetch('/api/times').then(r => r.ok ? r.json() : [])
    ]).then(([m, l, t]) => {
      setMasters(m);
      setLocations(l);
      setTimes(t);
    }).catch(err => console.error('Ошибка загрузки данных:', err));
    
    const token = localStorage.getItem('token');
    fetch('/api/instructor/free-slots-counts', {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : {})
      .then(data => setFreeSlots(data))
      .catch(err => console.error('Ошибка загрузки счётчиков:', err));
  }, []);
  
  // Загрузка записей
  const loadBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        from: format(startDate, 'yyyy-MM-dd'),
        to: format(endDate, 'yyyy-MM-dd'),
      });
      
      if (filterInstructor !== 'all') params.append('instructorId', filterInstructor);
      if (filterType !== 'all') params.append('type', filterType);
      if (filterLocation !== 'all') params.append('location', filterLocation);
      
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/staff-calendar?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        const regularBookings = data.filter(b => !b.isNoShow);
        const noShowBookings = data.filter(b => b.isNoShow);
        
        setBookings(regularBookings);
        setNoShows(noShowBookings);
      }
    } catch (err) {
      console.error('Ошибка загрузки записей:', err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, filterInstructor, filterType, filterLocation]);
  
  useEffect(() => {
    loadBookings();
  }, [loadBookings]);
  
  // Прокрутка к сегодняшнему дню
  useEffect(() => {
    if (!loading && !hasScrolledToToday.current) {
      const timer = setTimeout(() => {
        const todayElement = document.querySelector('[data-today="true"]');
        if (todayElement && scrollContainerRef.current) {
          todayElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          hasScrolledToToday.current = true;
        }
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [loading]);
  
  // Группировка записей по дате и времени
  const bookingsByDateAndTime = useMemo(() => {
    const grouped = {};
    
    bookings.forEach(b => {
      if (!grouped[b.date]) grouped[b.date] = {};
      if (!grouped[b.date][b.time]) grouped[b.date][b.time] = [];
      grouped[b.date][b.time].push({ ...b, isNoShow: false });
    });
    
    noShows.forEach(b => {
      if (!grouped[b.date]) grouped[b.date] = {};
      if (!grouped[b.date][b.time]) grouped[b.date][b.time] = [];
      grouped[b.date][b.time].push({ ...b, isNoShow: true });
    });
    
    return grouped;
  }, [bookings, noShows]);
  
  // 🔥 Обработка клика по слоту
  const handleSlotClick = useCallback((date, time, slotBookings) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    
    // Если слот свободный И выбран конкретный инструктор — открываем модалку для новой записи
    if ((!slotBookings || slotBookings.length === 0) && isSpecificInstructor) {
      setPickerMode('new-booking');
      setPickerInstructorId(filterInstructor);
      setPickerBookingData({ 
        date: dateStr, 
        time, 
        place: locations[0] || '' 
      });
      setShowStudentPicker(true);
      return;
    }
    
    // Если есть записи — показываем модалку с информацией
    if (slotBookings && slotBookings.length > 0) {
      setSelectedSlot({ date: dateStr, time, bookings: slotBookings });
      setExpandedInstructors({});
    }
  }, [filterInstructor, isSpecificInstructor, locations]);
  
  // Обработка выбора ученика из модалки
  const handleStudentSelect = (student) => {
    setShowStudentPicker(false);
    
    const params = new URLSearchParams({
      studentId: student.id,
      date: pickerBookingData.date,
      time: pickerBookingData.time,
      place: pickerBookingData.place,
      instructorId: pickerInstructorId,
      mode: pickerMode
    });
    
    navigate(`/editor-user?${params.toString()}`);
  };
  
  // Обработка кнопки "Переназначить"
  const handleReassign = (booking) => {
    setSelectedSlot(null);
    setPickerMode('reassign');
    setPickerInstructorId(booking.instructorId);
    setPickerBookingData({ 
      date: booking.date, 
      time: booking.time, 
      place: booking.place 
    });
    setShowStudentPicker(true);
  };
  
  // Обработка кнопки "Ученик не явился"
  const handleNoShow = async (booking) => {
    if (!window.confirm('Отметить ученика как неявившегося? Запись будет удалена.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/staff/no-show', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          instructorId: booking.instructorId,
          bookingId: booking.id
        })
      });
      
      if (res.ok) {
        await loadBookings();
        setSelectedSlot(null);
        alert('Неявка отмечена');
      } else {
        const errData = await res.json();
        alert(errData.message || 'Ошибка отметки неявки');
      }
    } catch (err) {
      console.error('Ошибка отметки неявки:', err);
      alert('Ошибка соединения с сервером');
    }
  };
  
  const toggleInstructor = useCallback((instructorId) => {
    setExpandedInstructors(prev => ({ ...prev, [instructorId]: !prev[instructorId] }));
  }, []);
  
  const resetFilters = () => {
    setFilterInstructor('all');
    setFilterType('all');
    setFilterLocation('all');
  };
  
  const goToToday = () => {
    const todayElement = document.querySelector('[data-today="true"]');
    if (todayElement && scrollContainerRef.current) {
      todayElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };
  
  // 🔥 Получаем имя выбранного инструктора (для модалки)
  const selectedInstructorName = useMemo(() => {
    if (!isSpecificInstructor) return null;
    const m = masters.find(m => m.id === filterInstructor);
    return m?.fullName || 'Инструктор';
  }, [isSpecificInstructor, filterInstructor, masters]);
  
  // Генерация месяцев (весь текущий год)
  const months = useMemo(() => {
    const result = [];
    const d = new Date(startDate);
    
    while (d <= endDate) {
      const monthStart = startOfMonth(d);
      const monthEnd = endOfMonth(d);
      const daysInMonth = [];
      
      const firstDayOfWeek = getDay(monthStart);
      const startOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
      
      for (let i = 0; i < startOffset; i++) {
        daysInMonth.push(null);
      }
      
      const current = new Date(monthStart);
      while (current <= monthEnd) {
        daysInMonth.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      
      while (daysInMonth.length % 7 !== 0) {
        daysInMonth.push(null);
      }
      
      result.push({
        year: monthStart.getFullYear(),
        month: monthStart.getMonth(),
        name: format(monthStart, 'LLLL yyyy', { locale: ru }),
        days: daysInMonth
      });
      
      d.setMonth(d.getMonth() + 1);
    }
    
    return result;
  }, [startDate, endDate]);
  
  if (user?.status !== 'staff') {
    return <div className="text-red-500 text-center mt-20 text-2xl">Доступ запрещён</div>;
  }
  
  return (
    <div className="min-h-screen bg-[#1b1b1b] text-[#dedede] pb-20">
      <Header />
      
      <div className="max-w-[1600px] mx-auto px-4 pt-24">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <CalendarDays className="text-[#b8975a]" size={32} />
            <div>
              <h1 className="text-2xl font-bold text-[#dedede]">Календарь записей</h1>
              <p className="text-sm text-gray-400">
                {format(startDate, 'yyyy')} год
                {isSpecificInstructor ? (
                  <span className="ml-2 text-[#b8975a]">• Режим: все слоты + действия</span>
                ) : (
                  <span className="ml-2 text-gray-500">• Режим: только занятые (просмотр)</span>
                )}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={goToToday}
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-[#b8975a] text-sm font-medium hover:bg-white/10 transition"
            >
              Сегодня
            </button>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-slate-300 text-sm font-medium hover:bg-white/10 transition flex items-center gap-2"
            >
              <Filter size={16} />
              Фильтры
              {(filterInstructor !== 'all' || filterType !== 'all' || filterLocation !== 'all') && (
                <span className="ml-1 px-1.5 py-0.5 bg-[#b8975a] text-black text-xs font-bold rounded-full">
                  {[filterInstructor, filterType, filterLocation].filter(f => f !== 'all').length}
                </span>
              )}
            </button>
            
            {(filterInstructor !== 'all' || filterType !== 'all' || filterLocation !== 'all') && (
              <button
                onClick={resetFilters}
                className="px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg text-sm font-medium hover:bg-red-500/20 transition"
              >
                Сбросить
              </button>
            )}
            
            <div className="ml-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-slate-300">
              Записей: <span className="font-medium text-[#b8975a]">{bookings.length}</span>
              {noShows.length > 0 && (
                <span className="ml-2 text-red-400">• Неявок: {noShows.length}</span>
              )}
            </div>
          </div>
        </div>
        
        {showFilters && (
          <div className="mb-6 bg-[#252525] rounded-lg border border-white/10 p-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wide">
                  Инструктор <span className="text-[#b8975a] normal-case">(своб. сегодня/завтра)</span>
                </label>
                <select
                  value={filterInstructor}
                  onChange={(e) => setFilterInstructor(e.target.value)}
                  className="w-full p-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-[#b8975a] focus:outline-none"
                >
                  <option value="all" className="bg-[#1b1b1b]">Все инструкторы</option>
                  {masters.map(m => {
                    const todayFree = freeSlots[m.id]?.today ?? 0;
                    const tomorrowFree = freeSlots[m.id]?.tomorrow ?? 0;
                    return (
                      <option key={m.id} value={m.id} className="bg-[#1b1b1b]">
                        {m.fullName} {todayFree} / {tomorrowFree}
                      </option>
                    );
                  })}
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wide">Тип записи</label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full p-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-[#b8975a] focus:outline-none"
                >
                  <option value="all" className="bg-[#1b1b1b]">Все типы</option>
                  <option value="regular" className="bg-[#1b1b1b]">Обычные</option>
                  <option value="dop" className="bg-[#1b1b1b]">Дополнительные</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wide">Место</label>
                <select
                  value={filterLocation}
                  onChange={(e) => setFilterLocation(e.target.value)}
                  className="w-full p-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-[#b8975a] focus:outline-none"
                >
                  <option value="all" className="bg-[#1b1b1b]">Все места</option>
                  {locations.map((loc, i) => (
                    <option key={i} value={loc} className="bg-[#1b1b1b]">{loc}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
        
        {/* КАЛЕНДАРЬ */}
        <div 
          ref={scrollContainerRef}
          className="max-h-[calc(100vh-260px)] overflow-y-auto"
        >
          <div className="min-w-[920px] space-y-6">
            {months.map(monthData => {
              const isCurrentMonth = monthData.year === today.getFullYear() && monthData.month === today.getMonth();
              const isPastMonth = monthData.year < today.getFullYear() || 
                                  (monthData.year === today.getFullYear() && monthData.month < today.getMonth());
              
              return (
                <div key={`${monthData.year}-${monthData.month}`} className="bg-white rounded-lg overflow-hidden shadow-2xl">
                  <div className={`px-6 py-3 border-b border-[#E8EAED] ${
                    isCurrentMonth ? 'bg-[#E8F0F7]' :
                    isPastMonth ? 'bg-[#D5D8DC]' :
                    'bg-[#E8E8E8]'
                  }`}>
                    <h2 className={`text-lg font-bold uppercase ${
                      isCurrentMonth ? 'text-[#5B8DBE]' :
                      isPastMonth ? 'text-[#6B7280]' :
                      'text-[#6B7280]'
                    }`}>
                      {monthData.name}
                    </h2>
                  </div>
                  
                  <div className="grid grid-cols-7">
                    {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((day) => (
                      <div 
                        key={day}
                        className="py-2 text-center text-xs font-semibold uppercase border-r border-b border-[#E8EAED] last:border-r-0 text-[#7A8288] bg-[#FAFBFC]"
                      >
                        {day}
                      </div>
                    ))}
                    
                    {monthData.days.map((day, idx) => {
                      if (!day) {
                        return (
                          <div 
                            key={`empty-${idx}`}
                            className="min-h-[140px] bg-[#E8EAED] border-r border-b border-[#D1D5DB] last:border-r-0"
                          ></div>
                        );
                      }
                      
                      const dateStr = format(day, 'yyyy-MM-dd');
                      const dayBookings = bookingsByDateAndTime[dateStr] || {};
                      
                      // 🔥 ЛОГИКА ОТОБРАЖЕНИЯ СЛОТОВ
                      let slotsToRender = [];
                      
                      if (isSpecificInstructor) {
                        // С фильтром: все слоты из times.json
                        slotsToRender = times.map(time => ({
                          time,
                          bookings: dayBookings[time] || []
                        }));
                      } else {
                        // 🔥 БЕЗ ФИЛЬТРА: только занятые слоты (сливаются)
                        const occupiedTimes = Object.keys(dayBookings).sort();
                        slotsToRender = occupiedTimes.map(time => ({
                          time,
                          bookings: dayBookings[time] || []
                        }));
                      }
                      
                      const totalBookings = slotsToRender.reduce((sum, s) => sum + s.bookings.length, 0);
                      
                      const isTodayDate = isToday(day);
                      const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                      
                      let dayBg = 'bg-white';
                      if (isPastMonth) {
                        dayBg = 'bg-[#D1D5DB]';
                      }
                      
                      return (
                        <div
                          key={dateStr}
                          data-today={isTodayDate ? 'true' : 'false'}
                          className={`min-h-[140px] p-1.5 border-r border-b border-[#E8EAED] last:border-r-0 transition-all ${dayBg} ${
                            isTodayDate ? 'ring-2 ring-[#5B8DBE]/50 ring-inset bg-[#E8F0F7]' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1.5 px-1">
                            <span className={`text-xs font-medium inline-flex items-center justify-center w-6 h-6 rounded-full ${
                              isTodayDate 
                                ? 'bg-[#5B8DBE] text-white' 
                                : isWeekend 
                                  ? 'text-[#C97B75]' 
                                  : isPastMonth
                                    ? 'text-[#6B7280]'
                                    : 'text-[#202124]'
                            }`}>
                              {format(day, 'd')}
                            </span>
                            {totalBookings > 0 && (
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                                isPastMonth
                                  ? 'bg-[#D1D5DB] text-[#6B7280]'
                                  : 'bg-[#E8F0F7] text-[#5B8DBE]'
                              }`}>
                                {totalBookings}
                              </span>
                            )}
                          </div>
                          
                          <div className="space-y-0.5">
                            {slotsToRender.map(({ time, bookings: slotBookings }) => {
                              // 🔥 Свободный слот (только когда выбран инструктор)
                              if (slotBookings.length === 0) {
                                return (
                                  <button
                                    key={time}
                                    onClick={() => handleSlotClick(day, time, slotBookings)}
                                    className="w-full px-1.5 py-1 rounded text-[10px] font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 transition border border-dashed border-gray-300"
                                    title={`${time} — Свободно. Нажмите для записи`}
                                  >
                                    {time}
                                  </button>
                                );
                              }
                              
                              // 🔥 Занятый слот
                              const hasNoShow = slotBookings.some(b => b.isNoShow);
                              const hasDop = slotBookings.some(b => b.type === 'dop' && !b.isNoShow);
                              const hasRegular = slotBookings.some(b => b.type === 'regular' && !b.isNoShow);
                              
                              let bgColor = 'bg-[#5B8DBE] hover:bg-[#4A7C9D]';
                              
                              if (hasNoShow) {
                                bgColor = 'bg-red-500 hover:bg-red-600';
                              } else if (hasDop && hasRegular) {
                                bgColor = 'bg-gradient-to-r from-[#5B8DBE] to-[#C97B75] hover:from-[#4A7C9D] hover:to-[#B86A65]';
                              } else if (hasDop) {
                                bgColor = 'bg-[#C97B75] hover:bg-[#B86A65]';
                              }
                              
                              if (isPastMonth) {
                                bgColor = bgColor.replace('#5B8DBE', '#9CA3AF').replace('#C97B75', '#B0A0A0');
                              }
                              
                              // 🔥 БЕЗ ФИЛЬТРА: плитки "сливаются" — показываем количество инструкторов
                              const instructorsCount = new Set(slotBookings.map(b => b.instructorId)).size;
                              
                              return (
                                <button
                                  key={time}
                                  onClick={() => handleSlotClick(day, time, slotBookings)}
                                  className={`w-full px-1.5 py-1 rounded text-[10px] font-medium text-white transition ${bgColor} truncate`}
                                  title={`${time} — ${slotBookings.length} записей от ${instructorsCount} инструкторов`}
                                >
                                  <div className="flex items-center justify-between gap-1">
                                    <span className="truncate">{time}</span>
                                    {hasNoShow && <span className="text-[9px]">❌</span>}
                                    {!isSpecificInstructor && slotBookings.length > 1 && !hasNoShow && (
                                      <span className="text-[9px] opacity-90 flex-shrink-0 bg-black/20 px-1 rounded">
                                        ×{instructorsCount}
                                      </span>
                                    )}
                                    {isSpecificInstructor && slotBookings.length > 1 && !hasNoShow && (
                                      <span className="text-[9px] opacity-80 flex-shrink-0">+{slotBookings.length - 1}</span>
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            
            <div className="h-12 flex items-center justify-center text-sm bg-[#252525] rounded-lg border border-white/10">
              {loading ? (
                <div className="flex items-center gap-2 text-[#5f6368]">
                  <div className="w-4 h-4 border-2 border-[#b8975a] border-t-transparent rounded-full animate-spin"></div>
                  <span>Загрузка...</span>
                </div>
              ) : (
                <span className="text-[#9aa0a6]">Календарь на {format(startDate, 'yyyy')} год</span>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* 🔥 Модалка слота — С ИМЕНЕМ ИНСТРУКТОРА */}
      {selectedSlot && (
        <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-[#E8EAED] px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-medium text-[#202124]">{selectedSlot.time}</h2>
                <p className="text-sm text-[#7A8288] mt-0.5">
                  {format(new Date(selectedSlot.date), 'EEEE, dd MMMM yyyy', { locale: ru })}
                </p>
                {!isSpecificInstructor && selectedSlot.bookings.length > 1 && (
                  <p className="text-xs text-[#5B8DBE] mt-1 font-medium">
                    👥 {new Set(selectedSlot.bookings.map(b => b.instructorId)).size} инструкторов в это время
                  </p>
                )}
              </div>
              <button
                onClick={() => setSelectedSlot(null)}
                className="p-2 text-[#7A8288] hover:bg-[#FAFBFC] rounded-full transition"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6">
              {isSpecificInstructor ? (
                // 🔥 С ФИЛЬТРОМ: показываем записи + кнопки действий
                <div className="space-y-2">
                  {selectedSlot.bookings.map((b, i) => (
                    <div key={i} className={`p-4 rounded-lg border ${
                      b.isNoShow ? 'bg-red-50 border-red-200' : 'bg-[#FAFBFC] border-[#E8EAED]'
                    }`}>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {b.isNoShow ? (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                            ❌ Неявка
                          </span>
                        ) : (
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            b.type === 'dop' ? 'bg-[#F5E8E6] text-[#C97B75]' : 'bg-[#E8F0F7] text-[#5B8DBE]'
                          }`}>
                            {b.type === 'dop' ? 'Доп.' : 'Обычная'}
                          </span>
                        )}
                      </div>
                      <div className="space-y-1 text-sm">
                        {/* 🔥 ИМЯ ИНСТРУКТОРА */}
                        <p>
                          <span className="text-[#7A8288]">Инструктор: </span>
                          <span className="font-medium text-[#202124]">{selectedInstructorName}</span>
                        </p>
                        <p><span className="text-[#7A8288]">Ученик:</span> <span className="font-medium text-[#202124]">{b.studentName}</span></p>
                        <p><span className="text-[#7A8288]">Место:</span> <span className="font-medium text-[#202124]">{b.place}</span></p>
                        {b.note && <p><span className="text-[#7A8288]">Примечание:</span> <span className="font-medium text-[#202124]">{b.note}</span></p>}
                      </div>
                      
                      {/* 🔥 Кнопки действий — ТОЛЬКО когда выбран инструктор */}
                      {!b.isNoShow && new Date(b.date) >= today && (
                        <div className="flex gap-2 mt-3 pt-3 border-t border-gray-200">
                          <button
                            onClick={() => handleReassign(b)}
                            className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition"
                          >
                            🔄 Переназначить
                          </button>
                          <button
                            onClick={() => handleNoShow(b)}
                            className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition"
                          >
                            ❌ Не явился
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                // 🔥 БЕЗ ФИЛЬТРА: только информация, БЕЗ кнопок, сгруппировано по инструкторам
                <div className="space-y-3">
                  {Object.entries(
                    selectedSlot.bookings.reduce((acc, b) => {
                      if (!acc[b.instructorId]) acc[b.instructorId] = { 
                        name: b.instructorName, 
                        bookings: [] 
                      };
                      acc[b.instructorId].bookings.push(b);
                      return acc;
                    }, {})
                  ).map(([instructorId, data]) => (
                    <div key={instructorId} className="bg-[#FAFBFC] rounded-lg border border-[#E8EAED] overflow-hidden">
                      {/* 🔥 ЗАГОЛОВОК С ИМЕНЕМ ИНСТРУКТОРА */}
                      <button
                        onClick={() => toggleInstructor(instructorId)}
                        className="w-full p-4 flex items-center justify-between hover:bg-[#F1F3F4] transition bg-white"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#E8F0F7] flex items-center justify-center text-[#5B8DBE] font-bold">
                            {data.name.charAt(0)}
                          </div>
                          <div className="text-left">
                            <span className="text-base font-bold text-[#202124] block">{data.name}</span>
                            <span className="text-xs text-[#7A8288]">
                              {data.bookings.length} {data.bookings.length === 1 ? 'запись' : 
                                data.bookings.length < 5 ? 'записи' : 'записей'}
                            </span>
                          </div>
                        </div>
                        {expandedInstructors[instructorId] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </button>
                      
                      {expandedInstructors[instructorId] && (
                        <div className="p-4 pt-0 space-y-2 border-t border-[#E8EAED]">
                          {data.bookings.map((b, i) => (
                            <div key={i} className={`p-3 rounded border ${
                              b.isNoShow ? 'bg-red-50 border-red-200' : 'bg-white border-[#E8EAED]'
                            }`}>
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                {b.isNoShow ? (
                                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                                    ❌ Неявка
                                  </span>
                                ) : (
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    b.type === 'dop' ? 'bg-[#F5E8E6] text-[#C97B75]' : 'bg-[#E8F0F7] text-[#5B8DBE]'
                                  }`}>
                                    {b.type === 'dop' ? 'Доп.' : 'Обычная'}
                                  </span>
                                )}
                              </div>
                              <div className="space-y-1 text-sm">
                                {/* 🔥 ИМЯ ИНСТРУКТОРА */}
                                <p>
                                  <span className="text-[#7A8288]">Инструктор: </span>
                                  <span className="font-medium text-[#202124]">{b.instructorName}</span>
                                </p>
                                <p><span className="text-[#7A8288]">Ученик:</span> <span className="font-medium text-[#202124]">{b.studentName}</span></p>
                                <p><span className="text-[#7A8288]">Место:</span> <span className="font-medium text-[#202124]">{b.place}</span></p>
                                {b.note && <p><span className="text-[#7A8288]">Примечание:</span> <span className="font-medium text-[#202124]">{b.note}</span></p>}
                              </div>
                              {/* 🔥 БЕЗ КНОПОК — только просмотр */}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Модалка выбора ученика */}
      {showStudentPicker && (
        <StudentPickerModal
          mode={pickerMode}
          instructorId={pickerInstructorId}
          bookingData={pickerBookingData}
          onSelect={handleStudentSelect}
          onClose={() => setShowStudentPicker(false)}
        />
      )}
      
      <BottomNav />
    </div>
  );
}